import { useState } from 'react';
import { useMultiTab } from './useMultiTab';

/**
 * 複数タブで開いているとき、**最新でない (非 owner) タブにだけ**出す非ブロッキングのソフト警告
 * （docs/03 §7.5-3 「複数タブ書込安全性の方針」 **層2 = UI 誘導**, 2026-06-06）。
 *
 * 設計方針:
 *   - **書込は止めない**（ハードブロックしない）。層1 (冪等 outbox) で書込自体は安全なため、
 *     ここでは「古いタブは最新の状態を取りこぼし得る」ことだけを伝え、1 タブへの集約を促す。
 *   - 「この画面を最新にする（再読み込み）」= リロードすると forceOwnership で lease を取り戻し、
 *     このタブが owner になる（= 警告が消える）。古いタブを最新化する正攻法。
 *   - × で閉じられる。再び別タブが開かれて警告条件が立ち上がったら、また表示する。
 *
 * 配置は AnnouncementBanner と同じ overlay パターン（親 main の relative に対して absolute top）。
 * announcement と同時表示は稀だが、運用上より緊急な本警告を上 (z-40) に重ねる。
 */
export function MultiTabBanner() {
  const { showWarning } = useMultiTab();
  const [dismissed, setDismissed] = useState(false);
  // 警告条件が一旦消えたら dismiss をリセットし、再発時にまた表示できるようにする。
  // effect を使わず render 中に前回値と比較して同期する (React 公式の「前回 render 情報の保持」パターン、
  // useOnlineStatus と同様に set-state-in-effect を避ける)。
  const [prevShowWarning, setPrevShowWarning] = useState(showWarning);
  if (showWarning !== prevShowWarning) {
    setPrevShowWarning(showWarning);
    if (!showWarning) setDismissed(false);
  }

  if (!showWarning || dismissed) return null;

  return (
    <div className="absolute top-0 left-0 right-0 z-40 p-2 pointer-events-none">
      <div
        className="bg-warning-600 text-white px-4 py-3 rounded shadow flex items-start gap-3 pointer-events-auto"
        role="status"
      >
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm">⚠️ この画面は複数開いています</p>
          <p className="text-sm whitespace-pre-wrap mt-1">
            このアプリを複数のタブ／ウィンドウで開いています。最新の状態は別の画面に表示されていて、この画面では新しい投稿などが反映されないことがあります。操作は1つの画面にまとめてください。
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-2 inline-flex items-center rounded bg-white/20 px-3 py-1.5 text-sm font-medium hover:bg-white/30 min-h-touch"
          >
            この画面を最新にする（再読み込み）
          </button>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-white text-xl px-2 min-h-touch min-w-touch shrink-0 hover:bg-white/10 rounded"
          aria-label="閉じる"
        >
          ×
        </button>
      </div>
    </div>
  );
}
